/**
 * Templates (docs/SPEC.md §11, §25.6): the starter set and the
 * organisation's own, for the composer's choice. `GET /v1/templates` →
 * `[{id, name, sentence, slots, aspect, manifest_url, source}]`: the
 * template's scenes at their defaults are played and framed in the browser
 * from its manifest (P28-T10), null until the hourly tick has baked one.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type { Aspect } from "./types";

export interface FilmTemplate {
  id: string;
  name: string;
  sentence: string;
  slots: unknown[];
  aspect: Aspect | null;
  manifest_url: string | null;
  source: string;
}

export const TEMPLATES_KEY = ["templates"] as const;

/** One entry of the list, or null when it is not a template of §11. */
export function asTemplate(raw: unknown): FilmTemplate | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Partial<FilmTemplate>;
  if (typeof t.id !== "string" || typeof t.name !== "string") return null;
  if (typeof t.sentence !== "string") return null;
  return {
    id: t.id,
    name: t.name,
    sentence: t.sentence,
    slots: Array.isArray(t.slots) ? t.slots : [],
    aspect:
      t.aspect === "9:16" ||
      t.aspect === "1:1" ||
      t.aspect === "16:9" ||
      t.aspect === "4:5"
        ? t.aspect
        : null,
    manifest_url: typeof t.manifest_url === "string" ? t.manifest_url : null,
    source: typeof t.source === "string" ? t.source : "starter",
  };
}

/** The list, or nothing: a composer without templates (the route not there
 *  yet, or none at all) offers none, it never fails for it. */
export function useTemplates(enabled = true) {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    enabled,
    queryFn: async () => {
      const answer = await api<unknown>("/v1/templates");
      const items = Array.isArray(answer)
        ? answer
        : ((answer as { items?: unknown[] } | null)?.items ?? []);
      return items.map(asTemplate).filter((t): t is FilmTemplate => !!t);
    },
    staleTime: 5 * 60_000,
  });
}
